import React, { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import apiClient from "../apiClient";
import "./EmployeeProfile.css";

const EmployeeProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employeeData, setEmployeeData] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteType, setNoteType] = useState("");
  const [filterType, setFilterType] = useState("");
  const [newNoteContent, setNewNoteContent] = useState("");
  const [benchData, setBenchData] = useState([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const mumFromQuery = queryParams.get("mum");
  console.log("EmployeeProfile - Received MUM:", mumFromQuery);

  // Check for authentication
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      navigate("/"); // Redirect to login if no token
    } else {
      try {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        if (decodedToken.exp < currentTime) {
          localStorage.removeItem("authToken");
          navigate("/");
        }
      } catch (error) {
        console.error("Invalid token:", error);
        localStorage.removeItem("authToken");
        navigate("/");
      }
    }
  }, [navigate]);

  // Fetch employee data
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        const response = await apiClient.get(`/api/employee-profile/${id}`);
        setEmployeeData(response.data);
      } catch (error) {
        console.error("Error fetching employee data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchEmployeeData();
    }
  }, [id]);

  // Fetch notes for the employee
  useEffect(() => {
    const fetchNotes = async () => {
      try {
        const url = filterType
          ? `/api/notes/${id}?note_type=${filterType}`
          : `/api/notes/${id}`;
        const response = await apiClient.get(url);
        setNotes(response.data.notes);
      } catch (error) {
        console.error("Error fetching notes:", error);
      }
    };

    if (id) {
      fetchNotes();
    }
  }, [id, filterType]);

  // Fetch "The Bench" data
  useEffect(() => {
    const fetchBenchData = async () => {
      try {
        const response = await apiClient.get(
          `/api/the-bench-table?profile_id=${encodeURIComponent(id)}`
        );
        setBenchData(response.data);
      } catch (error) {
        console.error("Error fetching The Bench data:", error);
      }
    };

    if (id) {
      fetchBenchData();
    }
  }, [id]);

  // Handle new note submission
  const handleAddNote = async () => {
    if (!noteType.trim()) {
      alert("Please select a Note Type!");
      return;
    }

    try {
      const response = await apiClient.post("/api/notes", {
        employee_id: id, // Employee ID
        created_by: 1, // Hardcoded for now, replace with logged-in user's ID if available
        note_type: noteType,
        content: newNoteContent,
        priority: "normal", // Default priority
      });

      // Add the new note to the list
      setNotes((prevNotes) => [response.data.note, ...prevNotes]); // `response.data.note` comes from the backend
      setNewNoteContent(""); // Clear the note content input
    } catch (error) {
      // Handle errors properly
      console.error(
        "Error creating note:",
        error.response?.data || error.message
      );
      alert("Failed to create note. Please try again.");
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  const handleBack = () => {
    console.log("Going back with MUM: ", mumFromQuery);
    if (mumFromQuery) {
      navigate(`/peopleplan?mum=${encodeURIComponent(mumFromQuery)}`);
    } else {
      navigate("/peopleplan");
    }
  };

  return (
    <div className="employee-profile-page">
      <div className="employee-profile-container">
        <div className="profile-and-notes">
          {/* Employee Profile Section */}
          <div className="profile-section">
            <button onClick={handleBack} className="back-button">
              Back
            </button>
            <h2>Employee Profile</h2>
            {employeeData ? (
              <table className="profile-table">
                <tbody>
                  <tr>
                    <th>Full Name</th>
                    <td>{employeeData.employee_name}</td>
                  </tr>
                  <tr>
                    <th>Store</th>
                    <td>{employeeData.store_name || "Not Assigned"}</td>
                  </tr>
                  <tr>
                    <th>Position</th>
                    <td>{employeeData.position_name || "Not Assigned"}</td>
                  </tr>
                  <tr>
                    <th>Category</th>
                    <td>{employeeData.category_name || "Not Assigned"}</td>
                  </tr>
                  <tr>
                    <th>Start Date</th>
                    <td>
                      {employeeData.hire_date || "Not Available"} (
                      {employeeData.years || 0} years {employeeData.months || 0}{" "}
                      months)
                    </td>
                  </tr>
                  <tr>
                    <th>Birth Date</th>
                    <td>
                      {employeeData.birth_date
                        ? `${employeeData.birth_date} (${
                            employeeData.b_years || 0
                          } years ${employeeData.b_months || 0} months)`
                        : "Not Available (0 years 0 months)"}
                    </td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <p>No employee data available.</p>
            )}
          </div>

          {/* Notes Section */}
          <div className="notes-section">
            <h2>Notes</h2>

            {/* Filter Notes by Type */}
            <div className="notes-controls">
              <label htmlFor="noteType">Select Note Type:</label>
              <select
                id="noteType"
                value={noteType}
                onChange={(e) => setNoteType(e.target.value)}
              >
                <option value="">Select</option>
                <option value="performance">Performance</option>
                <option value="attendance">Attendance</option>
                <option value="feedback">Feedback</option>
                <option value="disciplinary">Disciplinary</option>
              </select>

              {notes.length > 0 && (
                <>
                  <label htmlFor="filterType" style={{ marginLeft: "20px" }}>
                    Filter by Note Type:
                  </label>
                  <select
                    id="filterType"
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="performance">Performance</option>
                    <option value="attendance">Attendance</option>
                    <option value="feedback">Feedback</option>
                    <option value="disciplinary">Disciplinary</option>
                  </select>
                </>
              )}
            </div>

            {/* Formatting Toolbar */}
            <div className="formatting-toolbar">
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  document.execCommand("bold");
                }}
              >
                Bold
              </button>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  document.execCommand("italic");
                }}
              >
                Italic
              </button>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  document.execCommand("underline");
                }}
              >
                Underline
              </button>
            </div>

            {/* Add New Note */}
            <textarea
              className="note-content-editable"
              value={newNoteContent}
              onChange={(e) => setNewNoteContent(e.target.value)}
              placeholder="Write a note..."
            />
            <button className="submit-note-button" onClick={handleAddNote}>
              Submit Note
            </button>

            {/* Display Notes */}
            <div className="notes-list">
              {notes.length > 0 ? (
                notes.map((note) => (
                  <div key={note.note_id} className="note">
                    <p>
                      <strong>Type:</strong> {note.note_type}
                    </p>
                    <p>{note.content}</p>
                    <p>
                      <strong>Created By:</strong> {note.created_by} on{" "}
                      {new Date(note.created_at).toLocaleString()}
                    </p>
                    <p>
                      <strong>Priority:</strong> {note.priority}
                    </p>
                  </div>
                ))
              ) : (
                <p>No notes available for this employee.</p>
              )}
            </div>
          </div>
        </div>

        {/* The Bench Section */}
        <div className="bench-section">
          <h2>Workflows</h2>
          {Array.isArray(benchData) && benchData.length > 0 ? (
            benchData.map((location) => (
              <div
                key={location.corp}
                className="bench-location"
              >
                <h3>{location.corp}</h3>
                {Array.isArray(location.employees) &&
                location.employees.length > 0 ? (
                  location.employees.map((employee) => (
                    <div key={employee.user_id} className="bench-employee">
                      <p>
                        <strong>Name:</strong> {employee.employee_name} (
                        {employee.position_initials || "N/A"})
                      </p>
                      <p>
                        <strong>Hire Date:</strong>{" "}
                        {new Date(employee.hire_date).toLocaleDateString()}
                      </p>
                      <p>
                        <strong>Tenure:</strong> {employee.tenure.years} years{" "}
                        {employee.tenure.months} months
                      </p>
                      <p>
                        <strong>Workflow:</strong>{" "}
                        {employee.workflow_name || "N/A"}
                      </p>
                      {Array.isArray(employee.notes) &&
                      employee.notes.length > 0 ? (
                        <div className="bench-notes">
                          <h4>Notes:</h4>
                          {employee.notes.map((note) => (
                            <div key={note.note_id} className="bench-note">
                              <p>{note.content}</p>
                              <p>
                                <strong>Type:</strong> {note.note_type}, Created
                                by: {note.created_by} on{" "}
                                {new Date(note.created_at).toLocaleString()}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p>No notes available.</p>
                      )}
                    </div>
                  ))
                ) : (
                  <p>No employees available for this location.</p>
                )}
              </div>
            ))
          ) : (
            <p>No data available for The Bench.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeProfile;
